import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  computed,
  inject,
  signal
} from '@angular/core';
import { Project } from '@modules/projects/shared/models/project.model';
import { EBudgetType } from '@modules/project-creator/shared/enums/budget-type.enum';
import { StorageService } from '@core/services/storage.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ProjectStoreService } from '@modules/projects/shared/services/project-store.service';
import { SharedModule } from '@shared/shared.module';
import { Router } from '@angular/router';
import { ContactController } from '@modules/contacts/shared/controllers/contact.controller';
import { ContactProjectPemissionDialogData } from '@shared/components/contact-project-permission-modal/contact-project-permission-modal.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, switchMap, take, tap } from 'rxjs';
import { ProjectAPIService } from '@modules/projects/shared/services/project-api.service';
import { SidenavEventType, SidenavService } from '@shared/services/sidenav/sidenav.service';
import { SnackBarService } from '@core/services/snackbar.service';
import { buildContactCopyText, buildCustomerSectionCopyText, copyToClipboard } from './project-summary.helper';
import { ProjectSummaryDocumentsComponent } from './components/project-summary-documents/project-summary-documents.component';
import { ProjectSummaryLogbookComponent } from './components/project-summary-logbook/project-summary-logbook.component';

@Component({
  selector: 'project-summary',
  standalone: true,
  imports: [
    AngularSvgIconModule,
    DatePipe,
    TranslateModule,
    SharedModule,
    ProjectSummaryDocumentsComponent,
    ProjectSummaryLogbookComponent
  ],
  templateUrl: './project-summary.component.html',
  styleUrls: ['./project-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectSummaryComponent implements OnDestroy {
  private readonly storage = inject(StorageService);
  private readonly projectStore = inject(ProjectStoreService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly projectService = inject(ProjectAPIService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly translateService = inject(TranslateService);
  private sidenavService = inject(SidenavService);

  protected readonly isBudgetVisible = signal(false);
  private budgetVisibilityTimeoutId: number | null = null;

  readonly project = computed<Project | null>(() => this.projectStore.project() || null);

  protected getCurrency(type?: EBudgetType | string | null): string {
    const symbol = this.storage?.Company?.currency?.symbol || '€';
    return type === EBudgetType.BUDGET_TYPE_SQUARE ? `${symbol}/qm` : symbol;
  }

  protected toggleBudgetVisibility(): void {
    if (!this.isBudgetVisible()) {
      this.isBudgetVisible.set(true);
      this.startBudgetVisibilityTimeout();
      return;
    }

    this.hideBudget();
  }

  private startBudgetVisibilityTimeout(): void {
    this.clearBudgetVisibilityTimeout();
    this.budgetVisibilityTimeoutId = window.setTimeout(() => this.hideBudget(), 5_000);
  }

  private hideBudget(): void {
    this.isBudgetVisible.set(false);
    this.clearBudgetVisibilityTimeout();
  }

  private clearBudgetVisibilityTimeout(): void {
    if (!this.budgetVisibilityTimeoutId) return;
    window.clearTimeout(this.budgetVisibilityTimeoutId);
    this.budgetVisibilityTimeoutId = null;
  }

  protected editProject(): void {
    this.router.navigate(['/panel/projects/dashboard', this.project().id, 'configurator']);
  }

  protected editMainContact(): void {
    const ctrl = new ContactController();

    const data: ContactProjectPemissionDialogData = {
      projectId: this.project()?.id,
      mainContactId: this.project()?.MainContact?.id,
      responsibleContactId: this.project()?.basicDataBox?.responsibleContact?.id,
      contactList: [],
      getContactsList: true,
      project: this.project()
    };

    ctrl
      .editMainContactOrResponsible(data)
      .componentInstance.onProjectChange.pipe(
        tap((project) => this.projectStore.project.set(project)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected contactPreview(): void {
    const project = this.project();
    if (!project?.MainContact?.id || !project?.id) return;

    const ctrl = new ContactController(project.MainContact);
    ctrl.preview(project.MainContact.id);

    this.sidenavService.emitter
      .pipe(
        filter(({ type }) => type === SidenavEventType.CLOSE),
        take(1),
        switchMap(() => this.projectService.getProject(project.id)),
        tap((updatedProject) => this.projectStore.project.set(updatedProject)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected async copyValue(value: string | null | undefined): Promise<void> {
    const copied = await copyToClipboard(value || '');
    if (!copied) return;

    this.snackBarService.success(this.translateService.instant('Contacts.contactDataCopied'));
  }

  protected copyContactData(name?: string | null, phone?: string | null, email?: string | null): void {
    const contactText = buildContactCopyText(name, phone, email);
    this.copyValue(contactText);
  }

  protected copyCustomerSectionData(): void {
    const contact = this.project()?.MainContact;
    if (!contact) return;

    const customerText = buildCustomerSectionCopyText(
      contact.fullName,
      contact.fullAddress,
      contact.email,
      contact.fullPhone
    );

    this.copyValue(customerText);
  }

  ngOnDestroy(): void {
    this.clearBudgetVisibilityTimeout();
  }
}
